import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Receipt } from 'lucide-react'
import { toast } from 'sonner'
import { getLoanDetail, deleteLoanEntry } from '@/api/loans'
import { listAccounts } from '@/api/accounts'
import { listRecurring } from '@/api/recurring'
import { ROUTES } from '@/routes/routes'
import { ALL_TIME, transactionsLink } from '@/lib/transactionsLink'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { EditLoanModal, LoanIncreaseModal, LoanPaymentModal } from '@/components/loans/LoanModals'
import { LoanIconChip } from '@/components/loans/LoanCard'
import { LoanActionsBar } from '@/components/loans/LoanActionsBar'
import { HeroSplitBar } from '@/components/loans/LoansHero'
import { HERO_SHELL, HERO_TONE, useHeroFormat } from '@/components/loans/loanHero'
import { LoanHistory } from '@/components/loans/LoanHistory'
import { LoanPaymentsChart } from '@/components/loans/LoanPaymentsChart'
import { emiRecordedInMonth, groupByYear, withOutstandingAfter } from '@/components/loans/loanStats'
import { getDefaultAccountIcon } from '@/lib/accountIconOptions'
import { useHousehold } from '@/hooks/useHousehold'
import { formatShortDate } from '@/lib/formatDate'
import { cn } from '@/lib/utils'
import { RecurringModal } from './RecurringPage'

type OpenModal = 'EMI' | 'PREPAYMENT' | 'INCREASE' | 'EDIT' | null

const SECTION_LABEL = 'text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-2)]'

export default function LoanDetailPage() {
  const isReadOnly = useIsReadOnly()
  const { format } = useFormatCurrency()
  const heroFormat = useHeroFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const { data: household } = useHousehold()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<OpenModal>(null)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const {
    data: loan,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['loan', id],
    queryFn: () => getLoanDetail(id!),
    enabled: !!id,
  })
  const { data: accountsData } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  const { data: recurringData } = useQuery({
    queryKey: ['recurring', 'active', 'EXPENSE'],
    queryFn: () => listRecurring('active', 'EXPENSE'),
  })
  const accountName = (accountId: string | null) =>
    accountsData?.accounts.find((a) => a.id === accountId)?.name

  const deleteMutation = useMutation({
    mutationFn: (entryId: string) => deleteLoanEntry(id!, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan', id] })
      for (const key of ['accounts', 'transactions', 'dashboard', 'budget'])
        queryClient.invalidateQueries({ queryKey: [key] })
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail ?? 'Failed to delete. Please try again.')
    },
  })

  if (isLoading) {
    return <div className="p-6 text-[var(--fl-text-2)] text-sm">Loading…</div>
  }
  if (isError || !loan) {
    return <div className="p-6 text-[var(--fl-error)] text-sm">Failed to load loan details.</div>
  }

  const canEdit = !isReadOnly && !loan.archivedAt
  const repaid = loan.outstanding <= 0
  const today = new Date()
  const monthName = today.toLocaleDateString('en-IN', { month: 'long' })
  const account = accountsData?.accounts.find((a) => a.id === loan.id)
  const icon = account?.icon || getDefaultAccountIcon('LOAN', account?.subType)
  const from = accountName(loan.emiAccountId)
  const entries = withOutstandingAfter(loan.entries, loan.outstanding)
  const payments = loan.entries.filter((e) => e.type !== 'INCREASE').length
  const latestEmi = loan.entries.find((e) => e.type === 'EMI')
  const expectsEmi = !repaid && loan.emiAmount != null
  const recorded = expectsEmi && emiRecordedInMonth(loan.entries, today)
  const paid = loan.principalRepaid + loan.interestPaid
  const reminder = recurringData?.items.find((r) => r.loanAccountId === loan.id)

  const subtitle = [
    // Phone: one line, like the Loans card — the lender is usually in the name.
    wide && loan.lender,
    loan.emiAmount != null
      ? `EMI ${heroFormat(loan.emiAmount)}${from ? ` from ${from}` : ''}`
      : 'No EMI set',
  ]
    .filter(Boolean)
    .join(' · ')

  const menuItems: [string, string, OpenModal][] = [
    ...(!repaid
      ? [['Prepay', 'Pay extra principal', 'PREPAYMENT'] as [string, string, OpenModal]]
      : []),
    ['Increase outstanding', 'Top-up or extra disbursement', 'INCREASE'],
    ['Edit loan', 'Lender, EMI amount, paid-from account', 'EDIT'],
  ]

  return (
    <div className={cn('flex flex-col gap-3 p-4 md:gap-5 md:p-10', canEdit && 'pb-28 sm:pb-10')}>
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.APP.LOANS)}
          className="h-auto gap-1 px-0 py-0 text-sm text-[var(--fl-text-2)] hover:bg-transparent hover:text-[var(--fl-heading)]"
        >
          <ArrowLeft size={14} /> Loans
        </Button>
        <Link
          to={transactionsLink(
            { ...ALL_TIME, financialAccountId: loan.id },
            { back: ROUTES.APP.LOAN_DETAIL(loan.id) }
          )}
          className="text-sm font-medium text-[var(--fl-primary)] hover:underline"
        >
          Transactions →
        </Link>
      </div>

      <section
        aria-label="Loan summary"
        className={cn(HERO_SHELL, 'flex flex-col gap-2.5 md:gap-5')}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-2.5 md:gap-3.5">
            <LoanIconChip
              icon={icon}
              repaid={repaid}
              className="h-8 w-8 bg-[var(--fl-surface)] md:h-11 md:w-11"
            />
            <div className="min-w-0">
              <h1 className="break-words font-serif text-[17px] font-semibold leading-tight md:text-[26px]">
                {loan.name}
              </h1>
              <p className="mt-0.5 truncate text-xs text-[var(--fl-hero-fg-2)] md:whitespace-normal md:text-[13px]">
                {subtitle}
              </p>
            </div>
          </div>
          {canEdit && (
            <LoanActionsBar
              primary={repaid ? undefined : { label: 'Pay EMI', onClick: () => setOpen('EMI') }}
              items={menuItems.map(([label, hint, modal]) => ({
                label,
                hint,
                onSelect: () => setOpen(modal),
              }))}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 md:grid md:grid-cols-[1.4fr_1fr_1fr_0.6fr] md:items-end md:gap-7">
          <div className="flex items-baseline justify-between gap-3 md:block">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[13px] md:font-normal md:normal-case md:tracking-normal">
              {repaid ? 'Repaid' : 'Outstanding'}
            </p>
            <p
              data-testid="loan-outstanding"
              className={cn(
                'whitespace-nowrap font-serif text-[22px] font-bold leading-tight tracking-tight tabular-nums md:mt-1 md:text-[42px] md:font-semibold',
                repaid && HERO_TONE.income
              )}
            >
              {heroFormat(Math.max(loan.outstanding, 0))}
            </p>
          </div>
          <HeroSplitBar good={loan.principalRepaid} bad={loan.interestPaid} className="md:hidden" />
          <dl className="grid grid-cols-3 gap-2 md:contents">
            <div>
              <dt className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
                Principal repaid
              </dt>
              <dd className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-[var(--fl-hero-income)] md:mt-0.5 md:font-serif md:text-2xl">
                {heroFormat(loan.principalRepaid)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">Interest paid</dt>
              <dd className="whitespace-nowrap text-[13px] font-semibold tabular-nums text-[var(--fl-hero-expense)] md:mt-0.5 md:font-serif md:text-2xl">
                {heroFormat(loan.interestPaid)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">Payments</dt>
              <dd className="text-[13px] font-semibold tabular-nums md:mt-0.5 md:font-serif md:text-2xl">
                {payments}
              </dd>
            </div>
          </dl>
        </div>

        {paid > 0 && (
          <div className="hidden md:block">
            <HeroSplitBar good={loan.principalRepaid} bad={loan.interestPaid} />
            <p className="mt-1.5 text-xs text-[var(--fl-hero-fg-2)]">
              Paid {format(paid)} since you started tracking
            </p>
          </div>
        )}

        {repaid ? (
          <p className="text-[13px] text-[var(--fl-hero-fg-2)]">
            This loan is fully repaid. You can archive it from its account on the Accounts page.
          </p>
        ) : (
          (expectsEmi || (recurringData && (reminder || canEdit))) && (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-t border-[var(--fl-hero-bar-faint)] pt-2 md:pt-3">
              {expectsEmi ? (
                <p className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                      recorded
                        ? 'bg-[var(--fl-surface)] text-[var(--fl-income)]'
                        : 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]'
                    )}
                  >
                    {recorded ? `✓ ${monthName} EMI recorded` : `${monthName} EMI not recorded`}
                  </span>
                  {recorded && latestEmi && (
                    <span className="hidden text-xs text-[var(--fl-hero-fg-2)] md:inline">
                      {formatShortDate(latestEmi.date)} ·{' '}
                      {format(latestEmi.principalAmount + latestEmi.interestAmount)}
                    </span>
                  )}
                </p>
              ) : (
                <span />
              )}
              {recurringData &&
                (reminder ? (
                  <span className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
                    <span>EMI reminder · next due {formatShortDate(reminder.nextDueDate)}</span> ·{' '}
                    <Link to={ROUTES.APP.RECURRING} className="font-medium underline">
                      Manage
                    </Link>
                  </span>
                ) : (
                  canEdit && (
                    <button
                      type="button"
                      onClick={() => setShowReminderModal(true)}
                      className="text-xs font-medium underline md:text-[13px]"
                    >
                      Set up EMI reminder
                    </button>
                  )
                ))}
            </div>
          )
        )}
      </section>

      {entries.length === 0 ? (
        <section className="flex flex-col gap-2.5">
          <h2 className={SECTION_LABEL}>History</h2>
          <EmptyState
            compact
            icon={Receipt}
            title="Nothing recorded yet"
            description="Each EMI, prepayment or increase you record will appear here."
          />
        </section>
      ) : (
        <>
          <section className="flex flex-col gap-2.5">
            <h2 className={SECTION_LABEL}>Payments</h2>
            <LoanPaymentsChart entries={entries} />
          </section>
          <section className="flex flex-col gap-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className={SECTION_LABEL}>History</h2>
              <span className="text-xs text-[var(--fl-text-3)]">newest first</span>
            </div>
            {/* Keyed by the FY: its open year is seeded once, and the setting can arrive after the loan. */}
            <LoanHistory
              key={household?.financialYearStartMonth ?? 4}
              groups={groupByYear(entries, household?.financialYearStartMonth ?? 4)}
              accountName={accountName}
              onDelete={canEdit ? (entryId) => deleteMutation.mutate(entryId) : undefined}
              deleting={deleteMutation.isPending}
            />
          </section>
        </>
      )}

      {(open === 'EMI' || open === 'PREPAYMENT') && (
        <LoanPaymentModal
          loan={loan}
          type={open}
          // With a linked reminder, Pay EMI also marks it paid — otherwise the reminder stays
          // due and the same EMI is one tap away from being recorded twice.
          recurring={
            open === 'EMI' && reminder
              ? {
                  itemId: reminder.id,
                  dueDate: reminder.nextDueDate,
                  accountId: reminder.financialAccountId ?? undefined,
                  emiAmount: reminder.amount,
                }
              : undefined
          }
          onClose={() => setOpen(null)}
        />
      )}
      {open === 'INCREASE' && <LoanIncreaseModal loan={loan} onClose={() => setOpen(null)} />}
      {open === 'EDIT' && <EditLoanModal loan={loan} onClose={() => setOpen(null)} />}
      {showReminderModal && (
        <RecurringModal
          defaultType="EXPENSE"
          defaults={{
            name: `${loan.name} EMI`,
            kind: 'EMI',
            loanAccountId: loan.id,
            ...(loan.emiAmount != null ? { amount: loan.emiAmount } : {}),
            ...(loan.emiAccountId ? { financialAccountId: loan.emiAccountId } : {}),
          }}
          onClose={() => setShowReminderModal(false)}
        />
      )}
    </div>
  )
}
